import { nanoid } from 'nanoid';
import { useCallback, useState } from 'react';

/** A file queued for upload, identified by a stable client-side ID. */
export type QueuedFile = {
  clientId: string;
  file: File;
};

/** Constraints for an upload result tracked by the queue. */
export type UploadResult = {
  clientId: string;
};

/** Return type of the useUploadQueue hook. */
export type UseUploadQueueReturn<Result extends UploadResult> = {
  /** Files that have been queued for upload. */
  queuedFiles: QueuedFile[];
  /** Upload results that have been reported as complete. */
  completedUploads: Result[];
  /** Whether any queued files are still awaiting their upload result. */
  isUploading: boolean;
  /** Queue one or more files for upload, assigning each a stable client ID. */
  enqueueFiles: (files: File[]) => void;
  /** Record a completed upload result. Must include the `clientId` of the queued file. */
  reportUploadComplete: (result: Result) => void;
  /** Remove a queued file and its completed upload (if any) by client ID. */
  removeByClientId: (clientId: string) => () => void;
  /** Clear all queued files and completed uploads. */
  clearAll: () => void;
};

/**
 * Manages a queue of files pending upload alongside their resolved upload results.
 * Shared by components that need file-select -> upload -> collect-result workflows.
 */
const useUploadQueue = <Result extends UploadResult>(): UseUploadQueueReturn<Result> => {
  const [queuedFiles, setQueuedFiles] = useState<QueuedFile[]>([]);
  const [completedUploads, setCompletedUploads] = useState<Result[]>([]);

  const isUploading = completedUploads.length !== queuedFiles.length;

  const enqueueFiles = useCallback((files: File[]): void => {
    const withIds = files.map((file) => ({ clientId: nanoid(), file }));
    setQueuedFiles((previous) => [...previous, ...withIds]);
  }, []);

  const reportUploadComplete = useCallback((result: Result): void => {
    setCompletedUploads((previous) => [...previous, result]);
  }, []);

  const removeByClientId = useCallback(
    (clientId: string) => () => {
      setQueuedFiles((previous) => previous.filter((item) => item.clientId !== clientId));
      setCompletedUploads((previous) => previous.filter((item) => item.clientId !== clientId));
    },
    [],
  );

  const clearAll = useCallback((): void => {
    setQueuedFiles([]);
    setCompletedUploads([]);
  }, []);

  return {
    clearAll,
    completedUploads,
    enqueueFiles,
    isUploading,
    queuedFiles,
    removeByClientId,
    reportUploadComplete,
  };
};

export default useUploadQueue;
